/* global googletag */
import React, { useEffect, useRef, useId, useMemo } from "react";

const GPT_SRC = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";

function loadGPTScript() {
  if (typeof document === "undefined") return Promise.reject(new Error("no-document"));
  if (document.querySelector(`script[src^="${GPT_SRC}"]`)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = GPT_SRC;
    s.async = true;
    s.crossOrigin = "anonymous";
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

function ensureGPTGlobals() {
  const w = window;
  w.googletag = w.googletag || { cmd: [] };
  w.__gptGuard = w.__gptGuard || {
    servicesEnabled: false,
    slots: new Map(),
  };
  return w.__gptGuard;
}

const GoogleAds = ({
  adUnitPath = "/157165500,23206251223/MCM_Gj_Console/MCM_Gj_Console_banner_1",
  // ✅ correct default size
  sizes = [[728, 90]],
  divId,
  placeholderHeight = 90,
  emptyTimeout = 3000,
}) => {
  const containerRef = useRef(null);
  const ioRef = useRef(null);
  const timeoutRef = useRef(null);
  const handlerRef = useRef(null);

  const stateRef = useRef({
    hasFilledOnce: false,
    displayedOnce: false,
  });

  const reactId = useId();

  // Always-unique id per instance (even if same divId is passed)
  const safeDivId = useMemo(() => {
    const suffix = reactId.replace(/[:]/g, "");
    return divId ? `${divId}-${suffix}` : `gpt-${suffix}`;
  }, [divId, reactId]);

  const collapseContainer = () => {
    if (stateRef.current.hasFilledOnce) return;
    const el = containerRef.current;
    if (!el) return;
    el.classList.add("ad-empty");
    el.style.display = "none";
    el.style.height = "0";
    el.style.minHeight = "0";
    el.style.padding = "0";
    el.style.margin = "0";
    el.style.visibility = "hidden";
  };

  const showContainer = () => {
    const el = containerRef.current;
    if (!el) return;
    el.classList.remove("ad-empty");
    el.style.display = "flex";
    el.style.visibility = "visible";
    el.style.minHeight = `${placeholderHeight}px`;
    el.style.height = "auto";
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const el = containerRef.current;
    if (!el) return;

    el.style.minHeight = `${placeholderHeight}px`;
    el.style.display = "flex";
    el.style.justifyContent = "center";
    el.style.alignItems = "center";

    const setup = async () => {
      try {
        await loadGPTScript();
      } catch {
        collapseContainer();
        return;
      }

      const guard = ensureGPTGlobals();

      window.googletag.cmd.push(() => {
        const pubads = googletag.pubads();

        if (!guard.servicesEnabled) {
          pubads.enableSingleRequest();
          pubads.collapseEmptyDivs(true);
          googletag.enableServices();
          guard.servicesEnabled = true;
        }

        // Bind a listener per instance (and clean it up on unmount)
        const onRender = (event) => {
          const id = event.slot.getSlotElementId();
          if (id !== safeDivId) return;

          if (event.isEmpty) {
            if (!stateRef.current.hasFilledOnce) collapseContainer();
          } else {
            stateRef.current.hasFilledOnce = true;
            showContainer();
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
          }
        };
        handlerRef.current = onRender;
        pubads.addEventListener("slotRenderEnded", onRender);

        let slot = guard.slots.get(safeDivId);
        const divEl = document.getElementById(safeDivId);

        if (!slot) {
          try {
            slot = googletag.defineSlot(adUnitPath, sizes, safeDivId);
          } catch (e) {
            console.warn("defineSlot threw:", e);
          }
          if (!slot) {
            console.warn("defineSlot returned null. Check divId and adUnitPath.");
            collapseContainer();
            return;
          }
          slot.addService(pubads);
          guard.slots.set(safeDivId, slot);
        }

        if (divEl && divEl.childElementCount === 0 && !stateRef.current.displayedOnce) {
          try {
            googletag.display(safeDivId);
            stateRef.current.displayedOnce = true;
          } catch (e) {
            console.warn("googletag.display error:", e);
          }
        }

        if (!stateRef.current.hasFilledOnce) {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => {
            const node = document.getElementById(safeDivId);
            const ok = node && node.childElementCount > 0 && (node.offsetHeight || 0) > 0;
            ok ? showContainer() : collapseContainer();
          }, emptyTimeout);
        }
      });
    };

    try {
      ioRef.current = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              setup();
              ioRef.current?.disconnect();
              ioRef.current = null;
              break;
            }
          }
        },
        { rootMargin: "750px" }
      );
      ioRef.current.observe(el);
    } catch {
      setup();
    }

    return () => {
      if (ioRef.current) ioRef.current.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      // Clean event listener for this instance
      const pubads = window.googletag?.pubads?.();
      if (pubads && handlerRef.current) {
        pubads.removeEventListener?.("slotRenderEnded", handlerRef.current);
      }

      const guard = window.__gptGuard;
      if (guard) {
        const slot = guard.slots.get(safeDivId);
        if (slot && window.googletag?.destroySlots) {
          try {
            googletag.destroySlots([slot]);
          } catch {}
        }
        guard.slots.delete(safeDivId);
      }
      // Optional: visually collapse on unmount
      collapseContainer();
    };
  }, [adUnitPath, JSON.stringify(sizes), safeDivId, placeholderHeight, emptyTimeout]);

  // Use first declared size for min box
  const mw = Array.isArray(sizes?.[0]) ? sizes[0][0] : 728;
  const mh = Array.isArray(sizes?.[0]) ? sizes[0][1] : placeholderHeight;

  return (
    <div
      ref={containerRef}
      className="ad-container"
      style={{
        minHeight: `${placeholderHeight}px`,
        background: "transparent",
        overflow: "hidden",
        marginBottom: "5px",
        width: "100%",
      }}
    >
      <div
        id={safeDivId}
        style={{
          minWidth: mw || 728,            // ✅ fallback 728
          minHeight: mh || placeholderHeight,
        }}
      />
    </div>
  );
};

export default GoogleAds;
